import { createStore, type StoreApi } from 'zustand';

/**
 * Per-`messageSid` Zustand store for the citation wizard's **provide**
 * phase drafts (the "I have references in mind" entry).
 *
 * **What lives here:** a single Map of `statementIndex → value` covering
 * every provide row mounted for this message. One row, one free-text
 * value. At publish the value is routed by content — a URL attaches as a
 * verbatim reference, anything else becomes a help request (the routing
 * lives in `publishProvidedStatementReferences`; the store stays dumb
 * about it). One store per message lets the provide phase read
 * aggregated enablement (`selectHasPublishableContent`) without
 * subscribing to a variable number of per-statement stores.
 *
 * **What does NOT live here:** the persisted reference / help-request
 * arrays — those live in the statement-references API and are
 * written at publish time.
 */

type ProvideReferenceDraftState = {
  /** Keyed by `statementIndex`. Absent keys mean "empty row". */
  values: Map<number, string>;
};

type ProvideReferenceDraftActions = {
  /** Replace the draft value for the given statement. */
  setValueDraft: (statementIndex: number, next: string) => void;
  /** Reset to initial state. Called when the wizard enters the provide
   * phase or closes, so a reopened wizard never shows stale drafts. */
  reset: () => void;
};

export type ProvideReferenceDraftStore = ProvideReferenceDraftState & ProvideReferenceDraftActions;

export function createProvideReferenceDraftStore(): StoreApi<ProvideReferenceDraftStore> {
  return createStore<ProvideReferenceDraftStore>((set) => ({
    values: new Map(),

    setValueDraft: (statementIndex, next) =>
      set((state) => {
        const values = new Map(state.values);

        values.set(statementIndex, next);

        return { values };
      }),

    reset: () => set({ values: new Map() }),
  }));
}

/* ---------- Selectors -------------------------------------------------- */

/** Current draft value for the statement, or `''`. */
export function selectValueDraft(
  state: ProvideReferenceDraftStore,
  statementIndex: number
): string {
  return state.values.get(statementIndex) ?? '';
}

/**
 * True when at least one row has non-whitespace content — a URL or a
 * describe-it line. Drives the publish-enabled gate so an all-empty
 * provide phase can't publish a no-op.
 */
export function selectHasPublishableContent(state: ProvideReferenceDraftStore): boolean {
  return selectFilledCount(state) > 0;
}

/**
 * Count of rows with non-whitespace content — drives the
 * count-aware Publish CTA ("Publish N references").
 */
export function selectFilledCount(state: ProvideReferenceDraftStore): number {
  let count = 0;

  for (const value of state.values.values()) {
    if (value.trim().length > 0) {
      count += 1;
    }
  }

  return count;
}

/* ---------- Registry --------------------------------------------------- */

const storeMap = new Map<string, StoreApi<ProvideReferenceDraftStore>>();

export function getProvideReferenceDraftStore(
  messageSid: string
): StoreApi<ProvideReferenceDraftStore> {
  let store = storeMap.get(messageSid);

  if (!store) {
    store = createProvideReferenceDraftStore();

    storeMap.set(messageSid, store);
  }

  return store;
}

export function deleteProvideReferenceDraftStore(messageSid: string): void {
  storeMap.delete(messageSid);
}

/** Test-only: clear the registry between tests. */
export function __resetProvideReferenceDraftStoreRegistry(): void {
  storeMap.clear();
}
